import React, { useEffect, useState } from "react";
import { NewspaperIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import {
  fetchCategoryByType,
  fetchCategoryByTypePublic,
} from "../../features/admin/CategorySlice";
import CustomTableComponent from "../../components/CustomTableComponent";
import { useDispatch } from "react-redux";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};
const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "--";

// Modal chi tiết bài viết
const PostDetailModal = ({ post, loading, onClose }) => {
  if (!post) return null;
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4"
        {...animation}
      >
        <motion.div
          className="relative bg-white rounded-2xl max-w-7xl w-full p-8 shadow-xl border border-blue-100"
          style={{ maxHeight: "90vh", overflow: "auto" }}
          initial={{ scale: 0.96 }}
          animate={{ scale: 1, transition: { duration: 0.2 } }}
          exit={{ scale: 0.96, transition: { duration: 0.15 } }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 mb-6">
            {post.imageUrl && (
              <img
                src={
                  post.imageUrl?.startsWith("http")
                    ? post.imageUrl
                    : `${process.env.REACT_APP_API_URL}media/${post.imageUrl}`
                }
                alt="Ảnh bìa"
                className="w-20 h-14 rounded-lg object-cover border mr-3"
              />
            )}
            <NewspaperIcon className="h-6 w-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              {post.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
            <div className="space-y-2">
              <div>
                Trạng thái:{" "}
                <span
                  className={`font-medium ${
                    post.status === "published"
                      ? "text-green-700"
                      : "text-yellow-700"
                  }`}
                >
                  {post.status === "published" ? "Đã duyệt" : "Chờ duyệt"}
                </span>
              </div>
              <div>Ngày tạo: {formatDate(post.createdAt)}</div>
            </div>
            <div className="space-y-2">
              <div>
                Danh mục:{" "}
                <span className="text-blue-700 font-medium">
                  {post.category?.name || "--"}
                </span>
              </div>
              <div>
                Người tạo:{" "}
                <span className="font-medium">
                  {post.user?.userFullName || "--"}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Nội dung chi tiết
            </h3>
            <div
              className="text-sm max-h-[63vh] leading-relaxed text-gray-800 bg-gray-50 p-4 rounded-lg border"
              style={{
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{
                __html: post.content || "<i>Không có nội dung.</i>",
              }}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const PostsCard = () => {
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [catModal, setCatModal] = useState(null);
  const [loadingCat, setLoadingCat] = useState(false);
  const [detailPost, setDetailPost] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const dispatch = useDispatch();

  // Fetch all posts (không lấy type notification), fetch categories "navbar,post,"
  useEffect(() => {
    setLoading(true);
    Promise.all([
      ApiCustom.get("/posts?type!=notification"),
      userId == 1 || hasPermission("OVERVIEW")
        ? dispatch(fetchCategoryByTypePublic("navbar,post,"))
        : dispatch(fetchCategoryByType("navbar,post,")),
    ])
      .then(([postRes, catRes]) => {
        const postList =
          postRes?.data?.data?.data?.filter((p) => p.type !== "notification") ||
          [];
        const cats = catRes?.payload || catRes?.data?.data || [];
        const counts = {};
        postList.forEach((p) => {
          const cid = p.categoryId || p.category?.id;
          if (cid) counts[cid] = (counts[cid] || 0) + 1;
        });

        setTotal(postList.length);
        setCategories(
          cats.map((cat) => ({
            ...cat,
            postCount: counts[cat.id] || 0,
          })),
        );
        setPosts(postList);
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  const openCatModal = (cat) => {
    setCatModal(cat);
    setLoadingCat(true);
    setTimeout(() => {
      setFilteredPosts(
        posts.filter(
          (p) =>
            (p.categoryId === cat.id ||
              (p.category && p.category.id === cat.id)) &&
            p.type !== "notification",
        ),
      );
      setLoadingCat(false);
    }, 100);
  };

  const handleRowClick = async (post) => {
    setDetailLoading(true);
    setDetailPost(null);
    try {
      const res = await ApiCustom.get(`/posts/${post.id}`);
      setDetailPost(res?.data?.data || post);
    } catch {
      setDetailPost(post);
    } finally {
      setDetailLoading(false);
    }
  };

  // Table headers
  const postHeaders = [
    { key: "title", label: "Tiêu đề" },
    {
      key: "category",
      label: "Danh mục",
      render: (item) =>
        item.category
          ? item.category.name
          : categories.find((c) => c.id === item.categoryId)?.name || "--",
    },
    {
      key: "createdAt",
      label: "Ngày tạo",
      render: (item) =>
        item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("vi-VN")
          : "--",
    },
    {
      key: "user",
      label: "Người tạo",
      render: (item) => item.user?.userFullName || "--",
    },
  ];

  return (
    <div className="w-full">
      {/* Card style thống kê */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full h-full sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-blue-200 bg-gradient-to-br from-white to-blue-50/20 transition-all cursor-pointer shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-blue-100 rounded-full mb-3">
          <NewspaperIcon className="h-6 w-6 text-blue-600" />
        </div>
        <p className="text-lg text-black mb-1">Bài viết</p>
        <p className="text-3xl font-semibold ">{loading ? "..." : total}</p>
      </motion.div>

      {/* Modal danh mục bài viết */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-5xl mx-4 rounded-xl p-6 shadow-xl border border-blue-100"
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-xl font-semibold mb-6 text-center">
                Các danh mục bài viết
              </h2>
              {loading ? (
                <p className="text-gray-500">Đang tải...</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {categories.map((cat) => (
                    <motion.div
                      key={cat.id}
                      whileHover={{ scale: 1.04 }}
                      className="p-5 bg-white border border-blue-50 rounded-xl hover:border-blue-300 transition cursor-pointer flex flex-col items-center text-center"
                      onClick={() => openCatModal(cat)}
                    >
                      <div className="bg-blue-50 rounded-full p-3 mb-2">
                        <NewspaperIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-800">
                        {cat.name}
                      </h3>
                      <p className="text-xs text-gray-400">{cat.slug}</p>
                      <p className="text-2xl font-bold text-blue-700 mt-2">
                        {cat.postCount}
                      </p>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal danh sách bài viết */}
      <AnimatePresence>
        {catModal && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-7xl mx-4 rounded-xl p-6 shadow-xl border border-blue-100"
            >
              <button
                onClick={() => {
                  setCatModal(null);
                  setFilteredPosts([]);
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold mb-4">
                Danh sách: {catModal.name}
              </h2>
              {loadingCat ? (
                <p className="text-gray-500">Đang tải...</p>
              ) : (
                <CustomTableComponent
                  headers={postHeaders}
                  data={filteredPosts}
                  onRowClick={handleRowClick}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết bài viết */}
      <PostDetailModal
        post={detailPost}
        loading={detailLoading}
        onClose={() => setDetailPost(null)}
      />
    </div>
  );
};

export default PostsCard;
